// =============================================================================
// LP Express — Quiz Funnel renderer.
//
// Renders a `generatedContent.quiz` blob (see shared/lp-express-quiz.ts) as an
// interactive funnel on the public/published page, and as a stacked, fully
// editable layout inside the builder.
//
//  • Public (no editor):  intro screen → one scored question at a time with a
//    progress bar → a personalized result screen. Answers are tallied per
//    bucket and the highest-scoring bucket selects the result.
//  • Editor (editor present):  every screen is shown stacked and non-interactive
//    so the operator can edit ALL copy (intro, each question + option, each
//    result) in place via the existing path-based `setText` ops — the same
//    EditableText primitive the long-form template uses.
// =============================================================================
import { useMemo, useState } from "react";
import {
  ArrowRight, ArrowLeft, RotateCcw, Star, Check, Clock, HelpCircle, Sparkles,
  ShieldCheck, Truck, ChevronDown, BadgeCheck, Leaf, Sun, Moon, CalendarClock,
} from "lucide-react";
import {
  EditableText,
  EditableElement,
  EditableItem,
  LpReviewActions,
  useLpEditor,
  useLpReviewOverride,
  type LpReviewChoice,
} from "@/components/lp-editor";
import type { Product } from "@shared/schema";
import type { LpReceipt, LpReviewDisplay } from "@/lib/lp-express-pricing";
import {
  normalizeLpQuizContent,
  computeLpQuizResult,
  type LpQuizContent,
  type LpQuizAnswers,
  type LpQuizResultLabels,
} from "@shared/lp-express-quiz";

type QuizCtx = {
  brand: any;
  offer: { checkoutUrl?: string } | any;
  generatedContent: any;
  logoUrl: string | null;
  bundleProductPhotos: string[];
  // Result-page enrichment (all derived from the same real brand/product data
  // the long-form template uses, so the ending page matches the reference
  // without any extra generation). Every field is optional — each result
  // section renders only when its real data source exists.
  bundleImage?: string | null;
  products?: Product[];
  receipt?: LpReceipt;
  reviewDisplay?: LpReviewDisplay;
  ingredientRows?: { name: string; benefit?: string }[];
  typography?: { heading?: string; body?: string } | any;
};

const HEADING = "var(--color-brand-navy)";
const ACCENT = "var(--color-brand-green)";
// The quiz page sits on a near-white off-white to match the Papabear target
// (explicit value requested by the user), a hair warmer than pure white so it
// still separates from the pure-white stat cards.
const PAGE_BG = "rgb(250, 250, 247)";
const TEXT = "var(--color-brand-text)";
const MUTED = "var(--color-brand-muted)";
const CARD = "var(--color-brand-card)";
const BORDER = "var(--color-brand-lightborder)";
const GOLD = "var(--color-brand-gold)";

// Built-in defaults for the editable ending-page chrome labels. Shown verbatim
// until an operator rewords them; the override is stored on
// generatedContent.quiz.resultLabels (see shared/lp-express-quiz.ts).
const RESULT_LABEL_DEFAULTS = {
  recommendedBundle: "Your recommended bundle",
  whyFits: "Why this bundle fits you",
  dailyRoutine: "Your daily routine",
  realReviews: "Real reviews",
  realIngredients: "Real ingredients",
  storyEyebrow: "Real people, real place",
  storyHeading: "Why we started",
  commonQuestions: "Common questions",
} as const;

function RatingLine({
  value,
  path,
  editor,
}: {
  value: string;
  path: (string | number)[];
  editor: any;
}) {
  if (!value && !editor) return null;
  return (
    <div className="flex items-center justify-center gap-2" data-testid="quiz-rating-line">
      <span className="inline-flex" style={{ color: GOLD }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} className="h-4 w-4 fill-current" />
        ))}
      </span>
      <EditableText
        value={value}
        path={path}
        as="span"
        className="text-sm font-medium"
        style={{ color: MUTED }}
        placeholder="★ Loved by thousands"
        testId="text-quiz-rating"
      />
    </div>
  );
}

function CtaButton({
  label,
  path,
  onClick,
  href,
  editor,
  testId,
  fullWidth,
  hideArrow,
}: {
  label: string;
  path: (string | number)[];
  onClick?: () => void;
  href?: string;
  editor: any;
  testId: string;
  fullWidth?: boolean;
  hideArrow?: boolean;
}) {
  const inner = (
    <EditableText
      value={label}
      path={path}
      as="span"
      noHideDelete
      className={fullWidth ? "font-bold" : "font-semibold"}
      placeholder="Continue"
      testId={`${testId}-label`}
    />
  );
  const arrow = hideArrow ? null : <ArrowRight className="h-4 w-4" />;
  const cls = fullWidth
    ? "flex w-full items-center justify-center gap-2 rounded-xl px-8 py-4 text-lg shadow-sm transition-transform hover:-translate-y-0.5"
    : "inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-base shadow-sm transition-transform hover:-translate-y-0.5";
  const style = { backgroundColor: ACCENT, color: "#fff" };
  // In the editor we render a non-navigating button so clicking the label edits
  // it rather than advancing/navigating.
  if (editor) {
    return (
      <span className={cls} style={style} data-testid={testId}>
        {inner}
        {arrow}
      </span>
    );
  }
  if (href !== undefined) {
    const inert = !href;
    return (
      <a
        href={href || undefined}
        className={cls}
        style={{ ...style, ...(inert ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
        onClick={inert ? (e) => e.preventDefault() : undefined}
        data-testid={testId}
      >
        {inner}
        {arrow}
      </a>
    );
  }
  return (
    <button type="button" className={cls} style={style} onClick={onClick} data-testid={testId}>
      {inner}
      {arrow}
    </button>
  );
}

function IntroImages({
  quiz,
  ctx,
}: {
  quiz: LpQuizContent;
  ctx: QuizCtx;
}) {
  const imgs = (quiz.intro.images || []).filter(Boolean);
  const fallback = (ctx.bundleProductPhotos || []).filter(Boolean);
  // Combine (deduped) so a partial Sim image list is topped up from the
  // product's own photos — the Papabear target is a full 3-up row.
  const shown = Array.from(new Set([...imgs, ...fallback])).slice(0, 3);
  if (shown.length === 0) return null;
  // Adaptive columns so 1–2 images read as an intentional centered group
  // instead of a lone square stranded in a fixed 3-column grid.
  const gridCls =
    shown.length >= 3
      ? "grid-cols-3"
      : shown.length === 2
        ? "mx-auto max-w-sm grid-cols-2"
        : "mx-auto max-w-[240px] grid-cols-1";
  return (
    <div className={`grid w-full gap-2.5 ${gridCls}`} data-testid="quiz-intro-images">
      {shown.map((src, i) => (
        <div
          key={i}
          className="aspect-square overflow-hidden rounded-2xl border"
          style={{ borderColor: BORDER, backgroundColor: CARD }}
        >
          <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
        </div>
      ))}
    </div>
  );
}

const STAT_ICONS = [Clock, HelpCircle, Sparkles];

function StatCards({
  quiz,
  editor,
}: {
  quiz: LpQuizContent;
  editor: any;
}) {
  const stats = quiz.intro.stats || [];
  if (stats.length === 0 && !editor) return null;
  return (
    <div className="grid w-full grid-cols-3 gap-2.5" data-testid="quiz-stat-cards">
      {stats.map((s, i) => {
        const Icon = STAT_ICONS[i % STAT_ICONS.length];
        return (
          <div
            key={i}
            className="rounded-2xl border px-3 py-4 text-center shadow-sm"
            style={{ borderColor: BORDER, backgroundColor: CARD }}
            data-testid={`quiz-stat-${i}`}
          >
            <Icon className="mx-auto mb-2 h-4 w-4" style={{ color: MUTED }} strokeWidth={2} />
            <EditableText
              value={s.value}
              path={["quiz", "intro", "stats", i, "value"]}
              as="div"
              className="text-lg font-bold leading-none"
              style={{ color: HEADING }}
              placeholder="98%"
              testId={`text-quiz-stat-value-${i}`}
            />
            <EditableText
              value={s.label}
              path={["quiz", "intro", "stats", i, "label"]}
              as="div"
              className="mt-1.5 text-xs leading-snug"
              style={{ color: MUTED }}
              placeholder="reported softer skin"
              testId={`text-quiz-stat-label-${i}`}
            />
          </div>
        );
      })}
    </div>
  );
}

function IntroScreen({
  quiz,
  ctx,
  editor,
  onStart,
}: {
  quiz: LpQuizContent;
  ctx: QuizCtx;
  editor: any;
  onStart?: () => void;
}) {
  return (
    <div className="space-y-7 text-center" data-testid="quiz-intro">
      {ctx.logoUrl && (
        <img
          src={ctx.logoUrl}
          alt={ctx.brand?.name ?? "Brand"}
          className="mx-auto h-16 w-16 rounded-full object-contain"
          data-testid="img-quiz-logo"
        />
      )}
      <div className="space-y-4">
        {(quiz.intro.eyebrow || editor) && (
          <EditableText
            value={quiz.intro.eyebrow}
            path={["quiz", "intro", "eyebrow"]}
            as="div"
            className="text-xs font-semibold uppercase tracking-[0.22em]"
            style={{ color: MUTED }}
            placeholder="Find your match"
            testId="text-quiz-eyebrow"
          />
        )}
        <EditableText
          value={quiz.intro.headline}
          path={["quiz", "intro", "headline"]}
          as="h1"
          multiline
          className="text-3xl font-bold leading-[1.15] sm:text-4xl"
          style={{ color: HEADING }}
          placeholder="Take the 60-second quiz"
          testId="text-quiz-headline"
        />
        {(quiz.intro.subheadline || editor) && (
          <EditableText
            value={quiz.intro.subheadline}
            path={["quiz", "intro", "subheadline"]}
            as="p"
            multiline
            className="mx-auto max-w-md text-base leading-relaxed"
            style={{ color: MUTED }}
            placeholder="Answer a few quick questions and we'll build your perfect routine."
            testId="text-quiz-subheadline"
          />
        )}
      </div>
      <IntroImages quiz={quiz} ctx={ctx} />
      <StatCards quiz={quiz} editor={editor} />
      <div className="pt-1">
        <CtaButton
          label={quiz.intro.ctaText}
          path={["quiz", "intro", "ctaText"]}
          onClick={onStart}
          editor={editor}
          testId="button-quiz-start"
          fullWidth
          hideArrow
        />
      </div>
      <RatingLine value={quiz.intro.ratingLine} path={["quiz", "intro", "ratingLine"]} editor={editor} />
    </div>
  );
}

function QuestionCard({
  quiz,
  qIndex,
  editor,
  selectedOptionId,
  onAnswer,
}: {
  quiz: LpQuizContent;
  qIndex: number;
  editor: any;
  selectedOptionId?: string;
  onAnswer?: (optionId: string) => void;
}) {
  const q = quiz.questions[qIndex];
  if (!q) return null;
  return (
    <div className="space-y-6" data-testid={`quiz-question-${qIndex}`}>
      <EditableText
        value={q.prompt}
        path={["quiz", "questions", qIndex, "prompt"]}
        as="h2"
        multiline
        className="text-2xl font-bold leading-snug sm:text-3xl"
        style={{ color: HEADING }}
        placeholder="What's your top concern?"
        testId={`text-quiz-question-${qIndex}`}
      />
      <div className="flex flex-col gap-3">
        {q.options.map((opt, oIndex) => {
          const selected = selectedOptionId === opt.id;
          const optionInner = (
            <EditableText
              value={opt.label}
              path={["quiz", "questions", qIndex, "options", oIndex, "label"]}
              as="span"
              noHideDelete
              className="text-base font-medium"
              placeholder="Option"
              testId={`text-quiz-option-${qIndex}-${oIndex}`}
            />
          );
          const rowCls =
            "flex w-full items-center rounded-2xl border px-5 py-4 text-left transition-all";
          const rowStyle = {
            borderColor: selected ? ACCENT : BORDER,
            backgroundColor: CARD,
            color: TEXT,
            boxShadow: selected ? `0 0 0 1px ${ACCENT}` : undefined,
          };
          if (editor) {
            return (
              <div
                key={opt.id}
                className={rowCls}
                style={rowStyle}
                data-testid={`quiz-option-${qIndex}-${oIndex}`}
              >
                {optionInner}
              </div>
            );
          }
          return (
            <button
              type="button"
              key={opt.id}
              className={`${rowCls} hover:-translate-y-0.5 hover:shadow-md`}
              style={rowStyle}
              onClick={() => onAnswer?.(opt.id)}
              data-testid={`quiz-option-${qIndex}-${oIndex}`}
            >
              {optionInner}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Result-page helpers ──────────────────────────────────────────────────────
// The ending page mirrors the Papabear reference (bundle card + why-it-fits +
// daily routine + real reviews + real ingredients + brand story + FAQ + a sticky
// checkout band). Every section is DERIVED from the same real brand/product data
// the long-form template uses (offer/receipt pricing, product_reviews, product
// ingredients, brand founder story, expanded bundle members) — so a section only
// renders when its real source exists, never with fabricated placeholder data.

function SectionShell({
  children,
  testId,
  elementKey,
}: {
  children: React.ReactNode;
  testId?: string;
  // When set, the whole section becomes a hide/delete unit on the result page
  // (same EditableElement primitive the long-form template uses). Wrapping lives
  // here — inside the card's own null-guard — so a section with no real data
  // never renders an empty, hideable shell.
  elementKey?: string;
}) {
  const inner = (
    <div
      className="w-full rounded-3xl border p-6 text-left shadow-sm"
      style={{ borderColor: BORDER, backgroundColor: CARD }}
      data-testid={testId}
    >
      {children}
    </div>
  );
  if (elementKey) {
    return (
      <EditableElement elementKey={elementKey} testId={testId ? `element-${testId}` : undefined}>
        {inner}
      </EditableElement>
    );
  }
  return inner;
}

// A result-section eyebrow label. When `path` is supplied it becomes editable
// (the same EditableText primitive the rest of the template uses); the stored
// override lives on quiz.resultLabels. `formatOnly` keeps the format popover but
// leaves hide/delete to the enclosing section (SectionShell), so an eyebrow is
// never a stray hide/delete unit of its own.
function SectionEyebrow({
  children,
  color = ACCENT,
  path,
  value,
}: {
  children?: React.ReactNode;
  color?: string;
  path?: (string | number)[];
  value?: string;
}) {
  if (path) {
    return (
      <EditableText
        as="div"
        value={value}
        path={path}
        formatOnly
        className="text-xs font-semibold uppercase tracking-[0.2em]"
        style={{ color }}
        placeholder={typeof children === "string" ? children : undefined}
      />
    );
  }
  return (
    <div className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color }}>
      {children}
    </div>
  );
}

function Stars({ count = 5 }: { count?: number }) {
  return (
    <span className="inline-flex" style={{ color: GOLD }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className={`h-4 w-4 ${i < count ? "fill-current" : ""}`} />
      ))}
    </span>
  );
}

// Time-of-day labels for the "Your daily routine" step list, sized to the number
// of member SKUs in the recommended bundle.
function routineTimeLabels(n: number): string[] {
  if (n <= 1) return ["Daily"];
  if (n === 2) return ["Morning", "Evening"];
  if (n === 3) return ["Morning", "Anytime", "Evening"];
  return ["Morning", "Anytime", "Evening", "Weekly"];
}

function RoutineIcon({ label }: { label: string }) {
  const l = label.toLowerCase();
  const Icon = l.includes("morning")
    ? Sun
    : l.includes("evening") || l.includes("night")
      ? Moon
      : l.includes("week")
        ? CalendarClock
        : Sparkles;
  return <Icon className="h-4 w-4" style={{ color: ACCENT }} />;
}

function TrustLine({ icon: Icon, children }: { icon: any; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm" style={{ color: TEXT }}>
      <Icon className="h-4 w-4 shrink-0" style={{ color: ACCENT }} />
      <span>{children}</span>
    </div>
  );
}

// Recommended-bundle card: product image + savings badge + name + price
// (today / struck retail) + real trust lines + the primary checkout CTA.
function BundleCard({
  r,
  rIndex,
  ctx,
  editor,
  labels,
}: {
  r: LpQuizContent["results"][number];
  rIndex: number;
  ctx: QuizCtx;
  editor: any;
  labels?: LpQuizResultLabels;
}) {
  const checkoutUrl = (ctx.offer?.checkoutUrl ?? "") as string;
  const receipt = ctx.receipt;
  const offer = ctx.offer ?? {};
  const img = ctx.bundleImage ?? ctx.bundleProductPhotos?.[0] ?? null;
  const priceToday = (receipt?.priceTodayLabel || offer.priceToday || "") as string;
  const retail = (receipt?.retailLabel || "") as string;
  const pctRaw = receipt?.discountPercent ?? (offer.discountPercent ? Number(offer.discountPercent) : null);
  const pct = typeof pctRaw === "number" && Number.isFinite(pctRaw) && pctRaw > 0 ? pctRaw : null;
  const guaranteeDays = receipt?.guaranteeDays ?? offer.guaranteeDays ?? null;
  const shipping = (offer.shipping || "") as string;
  return (
    <SectionShell testId={`quiz-result-bundle-${rIndex}`}>
      {img && (
        <div className="relative mb-5 overflow-hidden rounded-2xl" style={{ backgroundColor: PAGE_BG }}>
          <img
            src={img}
            alt=""
            className="mx-auto aspect-square w-full max-w-[320px] object-contain"
            data-testid={`img-quiz-bundle-${rIndex}`}
          />
          {pct && (
            <span
              className="absolute right-3 top-3 rounded-full px-2.5 py-1 text-xs font-bold text-white"
              style={{ backgroundColor: "var(--color-brand-coral)" }}
              data-testid={`badge-quiz-savings-${rIndex}`}
            >
              SAVE {pct}%
            </span>
          )}
        </div>
      )}
      <SectionEyebrow
        path={["quiz", "resultLabels", "recommendedBundle"]}
        value={labels?.recommendedBundle || RESULT_LABEL_DEFAULTS.recommendedBundle}
      >
        Your recommended bundle
      </SectionEyebrow>
      <EditableText
        value={r.recommendation}
        path={["quiz", "results", rIndex, "recommendation"]}
        as="div"
        multiline
        className="mt-1 text-2xl font-bold leading-tight"
        style={{ color: HEADING }}
        placeholder="The Complete Routine Bundle"
        testId={`text-quiz-result-recommendation-${rIndex}`}
      />
      {(priceToday || retail) && (
        <div className="mt-3 flex items-baseline gap-2">
          {priceToday && (
            <span className="text-3xl font-extrabold" style={{ color: HEADING }} data-testid={`text-quiz-price-${rIndex}`}>
              {priceToday}
            </span>
          )}
          {retail && (
            <span className="text-lg line-through" style={{ color: MUTED }} data-testid={`text-quiz-retail-${rIndex}`}>
              {retail}
            </span>
          )}
        </div>
      )}
      {(shipping || guaranteeDays) && (
        <div className="mt-3 space-y-1.5">
          {shipping && <TrustLine icon={Truck}>{shipping}</TrustLine>}
          {guaranteeDays ? (
            <TrustLine icon={ShieldCheck}>{guaranteeDays}-day money-back guarantee</TrustLine>
          ) : null}
        </div>
      )}
      <div className="mt-5">
        <CtaButton
          label={r.ctaText}
          path={["quiz", "results", rIndex, "ctaText"]}
          href={checkoutUrl}
          editor={editor}
          testId={`button-quiz-result-cta-${rIndex}`}
          fullWidth
        />
      </div>
    </SectionShell>
  );
}

function WhyFitsCard({
  r,
  rIndex,
  editor,
  labels,
}: {
  r: LpQuizContent["results"][number];
  rIndex: number;
  editor: any;
  labels?: LpQuizResultLabels;
}) {
  if (!(r.benefits.length > 0 || editor)) return null;
  return (
    <SectionShell elementKey={`quiz:result:${rIndex}:why`} testId={`quiz-result-why-${rIndex}`}>
      <SectionEyebrow
        path={["quiz", "resultLabels", "whyFits"]}
        value={labels?.whyFits || RESULT_LABEL_DEFAULTS.whyFits}
      >
        Why this bundle fits you
      </SectionEyebrow>
      <ul className="mt-3 space-y-2.5">
        {r.benefits.map((b, i) => (
          <li key={i} className="flex items-start gap-2.5" data-testid={`quiz-result-benefit-${rIndex}-${i}`}>
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: ACCENT }} />
            <EditableText
              value={b}
              path={["quiz", "results", rIndex, "benefits", i]}
              as="span"
              className="text-sm leading-relaxed"
              style={{ color: TEXT }}
              placeholder="Benefit"
              testId={`text-quiz-result-benefit-${rIndex}-${i}`}
            />
          </li>
        ))}
      </ul>
    </SectionShell>
  );
}

// "Your daily routine" — one step per member SKU of the recommended bundle,
// tagged with a time of day. Renders only when the bundle has member products.
function RoutineCard({
  ctx,
  rIndex,
  labels,
  quiz,
  editor,
}: {
  ctx: QuizCtx;
  rIndex: number;
  labels?: LpQuizResultLabels;
  quiz: LpQuizContent;
  editor: any;
}) {
  const members = (ctx.products ?? []).filter(Boolean).slice(0, 4);
  if (members.length === 0) return null;
  const timeLabels = routineTimeLabels(members.length);
  const overrides = quiz.routineOverrides ?? [];
  return (
    <SectionShell elementKey={`quiz:result:${rIndex}:routine`} testId={`quiz-result-routine-${rIndex}`}>
      <SectionEyebrow
        path={["quiz", "resultLabels", "dailyRoutine"]}
        value={labels?.dailyRoutine || RESULT_LABEL_DEFAULTS.dailyRoutine}
      >
        Your daily routine
      </SectionEyebrow>
      <div className="mt-4 space-y-4">
        {members.map((p, i) => {
          const ov = overrides[i];
          const label = ov?.label || timeLabels[i] || timeLabels[timeLabels.length - 1];
          const name = ov?.name || ((p as any)?.name ?? "Step");
          const instr = ov?.instruction || ((p as any)?.summary || "").trim();
          return (
            <div key={(p as any)?.id ?? i} className="flex items-start gap-3" data-testid={`quiz-routine-step-${rIndex}-${i}`}>
              <div
                className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: PAGE_BG }}
              >
                <RoutineIcon label={label} />
              </div>
              <div className="min-w-0">
                <EditableText
                  as="div"
                  value={label}
                  path={["quiz", "routineOverrides", i, "label"]}
                  className="text-[11px] font-semibold uppercase tracking-wider"
                  style={{ color: ACCENT }}
                  placeholder="Time of day"
                  testId={`text-quiz-routine-label-${rIndex}-${i}`}
                />
                <EditableText
                  as="div"
                  value={name}
                  path={["quiz", "routineOverrides", i, "name"]}
                  className="text-sm font-semibold"
                  style={{ color: HEADING }}
                  placeholder="Step name"
                  testId={`text-quiz-routine-name-${rIndex}-${i}`}
                />
                {(editor || instr) && (
                  <EditableText
                    as="p"
                    multiline
                    value={instr}
                    path={["quiz", "routineOverrides", i, "instruction"]}
                    className="mt-0.5 text-sm leading-relaxed"
                    style={{ color: MUTED }}
                    placeholder="How to use this step"
                    testId={`text-quiz-routine-instr-${rIndex}-${i}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
}

// A single review slot on the result page. Defaults to the Nth aggregated real
// review, but in the editor exposes the SAME review picker the long-form hero
// uses (LpReviewActions) so operators can feature any REAL review in this slot —
// honest by construction, since the options are the brand's real reviews. The
// chosen review is snapshotted on generatedContent.reviewOverrides[slotKey].
function QuizReviewSlot({
  rIndex,
  slot,
  options,
}: {
  rIndex: number;
  slot: number;
  options: LpReviewChoice[];
}) {
  const slotKey = `quiz:result:${rIndex}:review:${slot}`;
  const override = useLpReviewOverride(slotKey);
  const rv = override ?? options[slot];
  if (!rv) return null;
  return (
    <div
      className="group/lpreview relative rounded-2xl p-4"
      style={{ backgroundColor: PAGE_BG }}
      data-testid={`quiz-review-${rIndex}-${slot}`}
    >
      <LpReviewActions
        slotKey={slotKey}
        reviews={options}
        testIdPrefix={`quiz-review-${rIndex}-${slot}`}
      />
      <Stars count={rv.rating} />
      <p className="mt-2 text-sm leading-relaxed" style={{ color: TEXT }}>
        &ldquo;{rv.reviewText}&rdquo;
      </p>
      <div className="mt-2.5 flex items-center gap-2">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: HEADING }}
        >
          {(rv.reviewer ?? "?").trim().charAt(0).toUpperCase() || "?"}
        </span>
        <span className="text-sm font-semibold" style={{ color: HEADING }}>
          {rv.reviewer ?? "Verified customer"}
        </span>
        <span className="inline-flex items-center gap-1 text-xs" style={{ color: ACCENT }}>
          <BadgeCheck className="h-3.5 w-3.5" />
          Verified
        </span>
      </div>
    </div>
  );
}

// "Real reviews" — up to three real customer reviews aggregated across the
// bundle's products (same source + picker the long-form template features).
// Hidden when the brand has no real reviews.
function ReviewsCard({
  ctx,
  rIndex,
  labels,
}: {
  ctx: QuizCtx;
  rIndex: number;
  labels?: LpQuizResultLabels;
}) {
  const rd = ctx.reviewDisplay;
  if (!rd || !rd.hasReviews || rd.allReviews.length === 0) return null;
  const options = rd.allReviews as LpReviewChoice[];
  const slotCount = Math.min(3, options.length);
  return (
    <SectionShell elementKey={`quiz:result:${rIndex}:reviews`} testId={`quiz-result-reviews-${rIndex}`}>
      <div className="flex items-center justify-between gap-3">
        <SectionEyebrow
          path={["quiz", "resultLabels", "realReviews"]}
          value={labels?.realReviews || RESULT_LABEL_DEFAULTS.realReviews}
        >
          Real reviews
        </SectionEyebrow>
        <div className="flex items-center gap-1.5">
          <Stars />
          <span className="text-sm font-semibold" style={{ color: HEADING }}>
            {rd.ratingText}
          </span>
          <span className="text-xs" style={{ color: MUTED }}>
            ({rd.reviewCountText})
          </span>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {Array.from({ length: slotCount }).map((_, i) => (
          <QuizReviewSlot key={i} rIndex={rIndex} slot={i} options={options} />
        ))}
      </div>
    </SectionShell>
  );
}

// "Real ingredients" — a 2-up grid of the bundle's ingredients/features.
// Hidden when no product carries ingredient data.
function IngredientsCard({
  ctx,
  rIndex,
  labels,
}: {
  ctx: QuizCtx;
  rIndex: number;
  labels?: LpQuizResultLabels;
}) {
  const rows = (ctx.ingredientRows ?? []).slice(0, 4);
  if (rows.length === 0) return null;
  return (
    <SectionShell elementKey={`quiz:result:${rIndex}:ingredients`} testId={`quiz-result-ingredients-${rIndex}`}>
      <SectionEyebrow
        path={["quiz", "resultLabels", "realIngredients"]}
        value={labels?.realIngredients || RESULT_LABEL_DEFAULTS.realIngredients}
      >
        Real ingredients
      </SectionEyebrow>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {rows.map((ing, i) => (
          <div
            key={i}
            className="rounded-2xl border p-4"
            style={{ borderColor: BORDER, backgroundColor: PAGE_BG }}
            data-testid={`quiz-ingredient-${rIndex}-${i}`}
          >
            <Leaf className="h-5 w-5" style={{ color: ACCENT }} />
            <div className="mt-2 text-sm font-semibold" style={{ color: HEADING }}>
              {ing.name}
            </div>
            {ing.benefit && (
              <p className="mt-0.5 text-xs leading-relaxed" style={{ color: MUTED }}>
                {ing.benefit}
              </p>
            )}
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

// Brand-story card — the founder story / mission, with the founder image when
// present. Hidden when the brand has no story text.
function BrandStoryCard({
  ctx,
  rIndex,
  labels,
}: {
  ctx: QuizCtx;
  rIndex: number;
  labels?: LpQuizResultLabels;
}) {
  const brand = ctx.brand ?? {};
  const story = ((brand.founderStory || brand.mission || "") as string).trim();
  if (!story) return null;
  const image = (brand.founderImage || brand.founderImageUrl || null) as string | null;
  const founderName = (brand.founderName || null) as string | null;
  return (
    <SectionShell elementKey={`quiz:result:${rIndex}:story`} testId={`quiz-result-story-${rIndex}`}>
      {image && (
        <div className="mb-5 overflow-hidden rounded-2xl" style={{ backgroundColor: PAGE_BG }}>
          <img src={image} alt={founderName ?? brand.name ?? "Our story"} className="aspect-[5/3] w-full object-cover" />
        </div>
      )}
      <SectionEyebrow
        path={["quiz", "resultLabels", "storyEyebrow"]}
        value={labels?.storyEyebrow || RESULT_LABEL_DEFAULTS.storyEyebrow}
      >
        Real people, real place
      </SectionEyebrow>
      <EditableText
        as="h3"
        value={labels?.storyHeading || RESULT_LABEL_DEFAULTS.storyHeading}
        path={["quiz", "resultLabels", "storyHeading"]}
        className="mt-1 text-xl font-bold"
        style={{ color: HEADING }}
        placeholder={RESULT_LABEL_DEFAULTS.storyHeading}
        testId={`text-quiz-story-heading-${rIndex}`}
      />
      <EditableText
        as="p"
        multiline
        value={labels?.storyBody || story}
        path={["quiz", "resultLabels", "storyBody"]}
        className="mt-2 whitespace-pre-line text-sm leading-relaxed"
        style={{ color: TEXT }}
        placeholder={story}
        testId={`text-quiz-story-body-${rIndex}`}
      />
      {founderName && (
        <div className="mt-3 text-xs font-semibold uppercase tracking-widest" style={{ color: HEADING }}>
          — {founderName}
        </div>
      )}
    </SectionShell>
  );
}

// FAQ built deterministically from REAL offer/product facts (routine, shipping,
// guarantee, ingredients) — no fabricated answers. Hidden when no fact applies.
function buildQuizFaq(ctx: QuizCtx): { q: string; a: string }[] {
  const out: { q: string; a: string }[] = [];
  const members = (ctx.products ?? []).filter(Boolean);
  const guaranteeDays = ctx.receipt?.guaranteeDays ?? ctx.offer?.guaranteeDays ?? null;
  const shipping = (ctx.offer?.shipping || "") as string;
  const ingredients = ctx.ingredientRows ?? [];
  if (members.length > 0) {
    const steps = routineTimeLabels(members.length);
    out.push({
      q: "How do I use it?",
      a:
        members.length > 1
          ? `Your kit is a simple ${members.length}-step routine — ${steps.join(", ")}. Each product is used at the time that suits it best.`
          : `Use it daily as directed for best results.`,
    });
  }
  if (shipping) out.push({ q: "What about shipping?", a: shipping });
  if (guaranteeDays)
    out.push({
      q: "Is it guaranteed?",
      a: `Yes — every order is backed by a ${guaranteeDays}-day money-back guarantee. If it isn't right for you, we'll make it right.`,
    });
  if (ingredients.length > 0)
    out.push({
      q: "What's inside?",
      a: `Key ingredients include ${ingredients.slice(0, 4).map((i) => i.name).join(", ")}.`,
    });
  return out;
}

function FaqItem({
  q,
  a,
  defaultOpen,
  editor,
  qPath,
  aPath,
}: {
  q: string;
  a: string;
  defaultOpen?: boolean;
  editor?: any;
  qPath?: (string | number)[];
  aPath?: (string | number)[];
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  // In the editor, render the question + answer as inline-editable text with no
  // collapse toggle (so both the question and its answer are always editable);
  // clicking to edit must not double as a show/hide toggle.
  if (editor && qPath && aPath) {
    return (
      <div className="py-3">
        <EditableText
          as="div"
          value={q}
          path={qPath}
          className="text-sm font-semibold"
          style={{ color: HEADING }}
          placeholder="Question"
          testId="text-quiz-faq-question"
        />
        <EditableText
          as="p"
          multiline
          value={a}
          path={aPath}
          className="mt-2 text-sm leading-relaxed"
          style={{ color: MUTED }}
          placeholder="Answer"
          testId="text-quiz-faq-answer"
        />
      </div>
    );
  }
  return (
    <div className="py-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 text-left"
        data-testid="button-quiz-faq-toggle"
      >
        <span className="text-sm font-semibold" style={{ color: HEADING }}>
          {q}
        </span>
        <ChevronDown
          className="h-4 w-4 shrink-0 transition-transform"
          style={{ color: MUTED, transform: open ? "rotate(180deg)" : "none" }}
        />
      </button>
      {open && (
        <p className="mt-2 text-sm leading-relaxed" style={{ color: MUTED }}>
          {a}
        </p>
      )}
    </div>
  );
}

function FaqCard({
  ctx,
  rIndex,
  editor,
  labels,
  quiz,
}: {
  ctx: QuizCtx;
  rIndex: number;
  editor: any;
  labels?: LpQuizResultLabels;
  quiz: LpQuizContent;
}) {
  const items = buildQuizFaq(ctx);
  if (items.length === 0) return null;
  const overrides = quiz.faqOverrides ?? [];
  return (
    <SectionShell elementKey={`quiz:result:${rIndex}:faq`} testId={`quiz-result-faq-${rIndex}`}>
      <SectionEyebrow
        path={["quiz", "resultLabels", "commonQuestions"]}
        value={labels?.commonQuestions || RESULT_LABEL_DEFAULTS.commonQuestions}
      >
        Common questions
      </SectionEyebrow>
      <div className="mt-1 divide-y" style={{ borderColor: BORDER }}>
        {items.map((it, i) => {
          const ov = overrides[i];
          return (
            <FaqItem
              key={i}
              q={ov?.q || it.q}
              a={ov?.a || it.a}
              defaultOpen={!!editor || i === 0}
              editor={editor}
              qPath={["quiz", "faqOverrides", i, "q"]}
              aPath={["quiz", "faqOverrides", i, "a"]}
            />
          );
        })}
      </div>
    </SectionShell>
  );
}

// Minimal sticky results header (logo + "Results") shown above the result
// screen on the public/published funnel.
function QuizResultHeader({ ctx }: { ctx: QuizCtx }) {
  return (
    <header
      className="sticky top-0 z-20"
      style={{ backgroundColor: CARD, borderBottom: `1px solid ${BORDER}` }}
      data-testid="quiz-result-header"
    >
      <div className="mx-auto flex max-w-lg items-center justify-between gap-4 px-5 py-3.5">
        {ctx.logoUrl ? (
          <img
            src={ctx.logoUrl}
            alt={ctx.brand?.name ?? "Brand"}
            className="h-9 w-9 rounded-full object-contain"
            data-testid="img-quiz-result-logo"
          />
        ) : (
          <span className="text-sm font-semibold" style={{ color: HEADING }}>
            {ctx.brand?.name ?? ""}
          </span>
        )}
        <span className="text-sm font-semibold" style={{ color: MUTED }}>
          Results
        </span>
      </div>
    </header>
  );
}

// Closing navy CTA card — mirrors the reference funnel's bottom checkout band:
// the price (today + struck retail), a short shipping/guarantee line, and the
// primary buy button. Rendered inline as the final result section (not sticky),
// matching the Papabear /quiz reference.
function BottomCtaCard({ r, rIndex, ctx }: { r: LpQuizContent["results"][number]; rIndex: number; ctx: QuizCtx }) {
  const checkoutUrl = (ctx.offer?.checkoutUrl ?? "") as string;
  const priceToday = (ctx.receipt?.priceTodayLabel || ctx.offer?.priceToday || "") as string;
  const retail = (ctx.receipt?.retailLabel || "") as string;
  const shipping = (ctx.offer?.shipping || "") as string;
  const guaranteeDays = ctx.receipt?.guaranteeDays ?? ctx.offer?.guaranteeDays ?? null;
  const subParts = [shipping, guaranteeDays ? `${guaranteeDays}-day guarantee` : ""].filter(Boolean);
  const inert = !checkoutUrl;
  return (
    <div
      className="rounded-2xl px-6 py-7 text-center"
      style={{ backgroundColor: HEADING }}
      data-testid={`quiz-result-bottom-cta-${rIndex}`}
    >
      {(priceToday || retail) && (
        <div className="flex items-baseline justify-center gap-2">
          {priceToday && <span className="text-2xl font-extrabold text-white">{priceToday}</span>}
          {retail && <span className="text-sm text-white/50 line-through">{retail}</span>}
        </div>
      )}
      {subParts.length > 0 && <p className="mt-1 text-xs text-white/70">{subParts.join(" · ")}</p>}
      <a
        href={inert ? undefined : checkoutUrl}
        onClick={inert ? (e) => e.preventDefault() : undefined}
        target={inert ? undefined : "_blank"}
        rel={inert ? undefined : "noopener noreferrer"}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-4 text-base font-bold text-white shadow-sm"
        style={{ backgroundColor: ACCENT, ...(inert ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
        data-testid={`button-quiz-bottom-cta-${rIndex}`}
      >
        {r.ctaText || "Shop your routine"}
        <ArrowRight className="h-4 w-4" />
      </a>
    </div>
  );
}

function ResultCard({
  quiz,
  rIndex,
  ctx,
  editor,
  onRestart,
}: {
  quiz: LpQuizContent;
  rIndex: number;
  ctx: QuizCtx;
  editor: any;
  onRestart?: () => void;
}) {
  const r = quiz.results[rIndex];
  if (!r) return null;
  const bucket = quiz.buckets.find((b) => b.key === r.bucketKey);
  const matchLabel = bucket?.label || "Your result";
  return (
    <div className="space-y-5" data-testid={`quiz-result-${rIndex}`}>
      <div className="space-y-3 text-center">
        <div
          className="inline-flex items-center gap-2 rounded-full border px-3 py-1"
          style={{ borderColor: BORDER, backgroundColor: CARD }}
          data-testid={`quiz-result-match-${rIndex}`}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: ACCENT }} />
          <span className="text-xs font-semibold uppercase tracking-[0.15em]" style={{ color: ACCENT }}>
            Your match: {matchLabel}
          </span>
        </div>
        <EditableText
          value={r.headline}
          path={["quiz", "results", rIndex, "headline"]}
          as="h2"
          multiline
          className="mx-auto max-w-2xl text-3xl font-bold leading-tight sm:text-4xl"
          style={{ color: HEADING }}
          placeholder="Your perfect match"
          testId={`text-quiz-result-headline-${rIndex}`}
        />
        {(r.description || editor) && (
          <EditableText
            value={r.description}
            path={["quiz", "results", rIndex, "description"]}
            as="p"
            multiline
            className="mx-auto max-w-xl text-lg"
            style={{ color: MUTED }}
            placeholder="Based on your answers, here's what we recommend."
            testId={`text-quiz-result-description-${rIndex}`}
          />
        )}
      </div>

      <BundleCard r={r} rIndex={rIndex} ctx={ctx} editor={editor} labels={quiz.resultLabels} />

      <div className="text-center">
        <RatingLine value={r.ratingLine} path={["quiz", "results", rIndex, "ratingLine"]} editor={editor} />
      </div>

      <WhyFitsCard r={r} rIndex={rIndex} editor={editor} labels={quiz.resultLabels} />
      <RoutineCard ctx={ctx} rIndex={rIndex} labels={quiz.resultLabels} quiz={quiz} editor={editor} />
      <ReviewsCard ctx={ctx} rIndex={rIndex} labels={quiz.resultLabels} />
      <IngredientsCard ctx={ctx} rIndex={rIndex} labels={quiz.resultLabels} />
      <BrandStoryCard ctx={ctx} rIndex={rIndex} labels={quiz.resultLabels} />
      <FaqCard ctx={ctx} rIndex={rIndex} editor={editor} labels={quiz.resultLabels} quiz={quiz} />

      <EditableElement
        elementKey={`quiz:result:${rIndex}:bottom-cta`}
        testId={`element-quiz-bottom-cta-${rIndex}`}
      >
        <BottomCtaCard r={r} rIndex={rIndex} ctx={ctx} />
      </EditableElement>

      {!editor && onRestart && (
        <div className="flex justify-center pt-1">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-sm font-medium"
            style={{ color: MUTED }}
            onClick={onRestart}
            data-testid="button-quiz-restart"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Retake the quiz
          </button>
        </div>
      )}
    </div>
  );
}

function QuizHeader({
  current,
  total,
  onBack,
  logoUrl,
  brandName,
}: {
  current: number;
  total: number;
  onBack: () => void;
  logoUrl: string | null;
  brandName?: string;
}) {
  const pct = total > 0 ? Math.round(((current + 1) / total) * 100) : 0;
  return (
    <header
      className="sticky top-0 z-20"
      style={{ backgroundColor: CARD, borderBottom: `1px solid ${BORDER}` }}
      data-testid="quiz-header"
    >
      <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-5 py-3.5">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm font-semibold transition-opacity hover:opacity-70"
          style={{ color: HEADING }}
          data-testid="button-quiz-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={brandName ?? "Brand"}
            className="h-9 w-9 rounded-full object-contain"
            data-testid="img-quiz-header-logo"
          />
        ) : (
          <span aria-hidden />
        )}
        <div className="text-sm" data-testid="text-quiz-counter">
          <span className="font-semibold" style={{ color: HEADING }}>
            {current + 1}
          </span>
          <span style={{ color: MUTED }}> / {total}</span>
        </div>
      </div>
      <div className="h-1.5 w-full" style={{ backgroundColor: BORDER }}>
        <div
          className="h-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: ACCENT }}
          data-testid="quiz-progress-fill"
        />
      </div>
    </header>
  );
}

function EditorSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mb-4 inline-block rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider"
      style={{ backgroundColor: CARD, borderColor: BORDER, color: MUTED }}
    >
      {children}
    </div>
  );
}

export default function LpExpressQuiz({ ctx }: { ctx: QuizCtx }) {
  const editor = useLpEditor();
  const quiz = useMemo<LpQuizContent>(
    () => normalizeLpQuizContent(ctx.generatedContent?.quiz),
    [ctx.generatedContent?.quiz],
  );

  // Questions the PUBLIC funnel walks through. The operator can hide a question
  // in the editor (it stays in the blob so it can be un-hidden); hidden ones are
  // skipped here — not shown, not scored (the shared tally skips them too).
  const visibleQuestions = useMemo(
    () => quiz.questions.filter((q) => !q.hidden),
    [quiz.questions],
  );

  // phase: -1 = intro, 0..n-1 = visible-question index, n = result screen.
  const total = visibleQuestions.length;
  const [phase, setPhase] = useState<number>(-1);
  const [answers, setAnswers] = useState<LpQuizAnswers>({});

  const wrapperCls = "mx-auto w-full max-w-lg px-5 py-12 sm:py-16";

  // ── Editor mode: everything stacked + editable, non-interactive. ──────────
  if (editor) {
    return (
      <div style={{ backgroundColor: PAGE_BG, color: TEXT }} data-testid="quiz-funnel-editor">
        <div className={wrapperCls}>
          <div className="space-y-16">
            <section>
              <EditorSectionLabel>Intro screen</EditorSectionLabel>
              <IntroScreen quiz={quiz} ctx={ctx} editor={editor} />
            </section>
            {quiz.questions.map((q, i) => (
              <section key={q.id} className="border-t pt-12" style={{ borderColor: BORDER }}>
                <EditableItem
                  path={["quiz", "questions"]}
                  index={i}
                  count={quiz.questions.length}
                  hidden={q.hidden}
                  testId={`quiz-question-item-${i}`}
                >
                  <EditorSectionLabel>
                    Question {i + 1}
                    {q.hidden ? " · hidden" : ""}
                  </EditorSectionLabel>
                  <QuestionCard quiz={quiz} qIndex={i} editor={editor} />
                </EditableItem>
              </section>
            ))}
            {quiz.results.map((r, i) => {
              const bucket = quiz.buckets.find((b) => b.key === r.bucketKey);
              return (
                <section key={i} className="border-t pt-12" style={{ borderColor: BORDER }}>
                  <EditorSectionLabel>
                    Result{bucket?.label ? `: ${bucket.label}` : r.bucketKey ? `: ${r.bucketKey}` : ""}
                  </EditorSectionLabel>
                  <ResultCard quiz={quiz} rIndex={i} ctx={ctx} editor={editor} />
                </section>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Public / published mode: interactive funnel. ──────────────────────────
  const answerQuestion = (optionId: string) => {
    const q = visibleQuestions[phase];
    if (!q) return;
    setAnswers((prev) => ({ ...prev, [q.id]: optionId }));
    setPhase((p) => p + 1); // advance to next question, or to the result screen
  };

  const goBack = () => setPhase((p) => Math.max(-1, p - 1));
  const restart = () => {
    setAnswers({});
    setPhase(-1);
  };

  // With no visible questions (every one hidden), the intro's Start goes straight
  // to the result (phase 0 ≥ total 0) rather than stranding on a dead Start.
  const showResult = total === 0 ? phase >= 0 : phase >= total;
  const { resultIndex } = showResult
    ? computeLpQuizResult(quiz, answers)
    : { resultIndex: -1 };

  const isQuestion = phase >= 0 && phase < total;
  // The current question's ORIGINAL index (into quiz.questions), so its testIds
  // and edit paths stay stable regardless of how many earlier ones are hidden.
  const currentQuestion = isQuestion ? visibleQuestions[phase] : undefined;
  const currentOriginalIndex = currentQuestion
    ? quiz.questions.indexOf(currentQuestion)
    : -1;

  const hasResult = showResult && resultIndex >= 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: PAGE_BG, color: TEXT }} data-testid="quiz-funnel">
      {isQuestion && (
        <QuizHeader
          current={phase}
          total={total}
          onBack={goBack}
          logoUrl={ctx.logoUrl}
          brandName={ctx.brand?.name}
        />
      )}
      {hasResult && <QuizResultHeader ctx={ctx} />}
      <div className={wrapperCls}>
        {phase < 0 && <IntroScreen quiz={quiz} ctx={ctx} editor={null} onStart={() => setPhase(0)} />}

        {isQuestion && (
          <QuestionCard
            quiz={quiz}
            qIndex={currentOriginalIndex}
            editor={null}
            selectedOptionId={answers[currentQuestion?.id ?? ""]}
            onAnswer={answerQuestion}
          />
        )}

        {hasResult && (
          <ResultCard quiz={quiz} rIndex={resultIndex} ctx={ctx} editor={null} onRestart={restart} />
        )}
      </div>
    </div>
  );
}
